import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RoleService } from '../auth/role-service';
import { ResponsiveBaseComponent, Role } from 'shared-lib';
import Keycloak from 'keycloak-js';
import { ParkerStateService } from '../services/parker-state.service';

interface BottomNavItem {
  id: string;
  labelKey: string;
  icon: string;
  route?: string;
  action?: () => void;
  requiresAuth?: boolean;
  role?: Role;
  badge?: () => number;
}

@Component({
  selector: 'app-bottom-nav-menu',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, TranslatePipe],
  templateUrl: './bottom-nav-menu.component.html',
  styleUrl: './bottom-nav-menu.component.scss'
})
export class BottomNavMenuComponent extends ResponsiveBaseComponent {
  private readonly router = inject(Router);
  private readonly roleService = inject(RoleService);
  private readonly keycloak = inject(Keycloak);
  readonly state = inject(ParkerStateService);

  readonly bottomNavItems: BottomNavItem[] = [
    {
      id: 'home',
      labelKey: 'NAV.HOME',
      icon: 'home',
      route: '/'
    },
    {
      id: 'activity',
      labelKey: 'NAV.ACTIVITY',
      icon: 'receipt_long',
      route: '/activity',
      requiresAuth: true,
      role: Role.RegisteredParker,
      badge: () => this.state.unpaidCount()
    },
    {
      id: 'license-plates',
      labelKey: 'NAV.VEHICLES',
      icon: 'directions_car',
      route: '/license-plates',
      requiresAuth: true,
      role: Role.RegisteredParker
    },
    {
      id: 'entitlements',
      labelKey: 'NAV.PERMISSIONS',
      icon: 'vpn_key',
      route: '/entitlements',
      requiresAuth: true,
      role: Role.RegisteredParker
    },
    {
      id: 'account',
      labelKey: 'NAV.ACCOUNT',
      icon: 'account_circle',
      route: '/my-account',
      requiresAuth: true
    },
    {
      id: 'register',
      labelKey: 'DASHBOARD.REGISTER_BTN',
      icon: 'how_to_reg',
      route: '/register',
      requiresAuth: false
    }
  ];

  get visibleItems(): BottomNavItem[] {
    return this.bottomNavItems.filter(item => this.isItemVisible(item));
  }

  private isItemVisible(item: BottomNavItem): boolean {
    const isAuthenticated = this.roleService.isAuthenticated();

    // Hide register for authenticated users
    if (item.id === 'register' && isAuthenticated) {
      return false;
    }

    // Show items that don't require auth
    if (!item.requiresAuth) {
      return true;
    }

    // Check authentication for auth required items
    if (item.requiresAuth && !isAuthenticated) {
      return false;
    }

    return true;
  }

  onItemClick(item: BottomNavItem): void {
    if (item.action) {
      item.action();
    } else if (item.route) {
      this.router.navigate([item.route]);
    }
  }

  isActive(item: BottomNavItem): boolean {
    if (!item.route) return false;
    if (item.route === '/') {
      return this.router.url === '/';
    }
    return this.router.url === item.route || this.router.url.startsWith(item.route + '/');
  }
}
